import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Trash2, ShieldCheck, FileArchive, FileText, CheckCircle2, Loader2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmtDateTime } from "@/lib/utils";

const STEPS = ["stepSource", "stepDatabase", "stepAssets", "stepDocs", "stepVerify"];
const mb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export default function BackupExport() {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/master/backups").then((r) => setHistory(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => {
    load();
  }, []);

  const create = async (label = "") => {
    if (!window.confirm(t("backupConfirm"))) return;
    setBusy(true);
    setResult(null);
    setStep(0);
    const timer = setInterval(() => setStep((s) => (s < STEPS.length - 1 ? s + 1 : s)), 1200);
    try {
      const { data } = await api.post("/master/backup", label ? { label } : {});
      setResult(data);
      setStep(STEPS.length);
      toast.success(t("backupReady"));
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      setStep(-1);
    } finally {
      clearInterval(timer);
      setBusy(false);
    }
  };

  const download = async (path, name) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(t("deleteBackupConfirm"))) return;
    try {
      await api.delete(`/master/backup/${b.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="backup-export-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <DatabaseBackup className="h-5 w-5 text-emerald-700" /> {t("backupTitle")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("backupSub")}</p>
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-rose-50 border border-rose-200 px-3 py-1 text-xs font-bold text-rose-800" data-testid="backup-master-only">
          <ShieldCheck className="h-3.5 w-3.5" /> {t("masterOnlyFeature")}
        </div>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="text-sm text-slate-600 leading-relaxed">
          Creates a genuine migration ZIP: complete frontend + backend source code, mongodump database backup + JSON exports, uploaded business images, deployment templates (Docker / nginx / systemd), installation and update scripts, README + DEPLOYMENT.md, DATABASE_SCHEMA.md, .env.example (placeholders only — no real secrets), PROJECT_DOCUMENTATION.pdf and MANIFEST.txt.
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button data-testid="create-backup-btn" className="sf-btn-primary !py-3.5 !text-base flex-1" onClick={() => create()} disabled={busy}>
            <DatabaseBackup className="h-5 w-5" /> {busy ? t("creatingBackup") : t("createBackup")}
          </button>
          <button data-testid="create-final-backup-btn" className="sf-btn-outline !py-3.5 !text-base flex-1" onClick={() => create("FULL_PROJECT_SOURCE_CODE_FINAL")} disabled={busy}>
            <FileArchive className="h-5 w-5" /> {t("createFinalBackup")}
          </button>
        </div>

        <div className="flex justify-end">
          <button data-testid="download-doc-page-btn" className="sf-link text-sm" onClick={() => download("/master/documentation", "PROJECT_DOCUMENTATION.pdf")}>
            <FileText className="h-4 w-4 inline mr-1" /> {t("downloadDoc")}
          </button>
        </div>

        {step >= 0 && (
          <ol className="space-y-1.5" data-testid="backup-progress">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-2 text-sm">
                {busy && i === step ? (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                ) : i < step || !busy ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <span className="h-4 w-4 rounded-full border-2 border-slate-300" />
                )}
                <span className={i < step || !busy ? "text-slate-600" : "font-semibold text-slate-900"}>{t(s)}</span>
              </li>
            ))}
          </ol>
        )}

        {result && (
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="backup-result">
            <div className="flex items-center gap-2 font-display font-bold text-emerald-900">
              <FileArchive className="h-5 w-5" /> <span data-testid="backup-result-filename">{result.filename}</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 text-sm text-emerald-900">
              <div data-testid="backup-result-date">{fmtDateTime(result.created_at)}</div>
              <div data-testid="backup-result-size">{mb(result.size_bytes)}</div>
              <div data-testid="backup-result-files">{result.files} files · {result.status}</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button data-testid="download-backup-btn" className="sf-btn-primary flex-1" onClick={() => download(`/master/backup/${result.id}/download`, result.filename)}>
                <Download className="h-4 w-4" /> {t("downloadFull")}
              </button>
              <button data-testid="download-doc-btn" className="sf-btn-outline flex-1" onClick={() => download("/master/documentation", "PROJECT_DOCUMENTATION.pdf")}>
                <FileText className="h-4 w-4" /> {t("downloadDoc")}
              </button>
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer font-bold text-slate-700">{t("backupIncluded")} ({result.included.length})</summary>
              <ul className="mt-2 space-y-0.5 text-xs text-slate-600 max-h-40 overflow-y-auto">
                {result.included.map((x, i) => <li key={i}>+ {x}</li>)}
              </ul>
            </details>
            {result.excluded.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-bold text-slate-700">{t("backupExcluded")} ({result.excluded.length})</summary>
                <ul className="mt-2 space-y-0.5 text-xs text-slate-600 max-h-40 overflow-y-auto">
                  {result.excluded.map((x, i) => <li key={i}>- {x}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="sf-card overflow-x-auto" data-testid="backup-history">
        <div className="p-5 pb-0 font-display font-bold text-slate-900">{t("backupHistory")}</div>
        <table className="w-full min-w-[640px] mt-3">
          <thead>
            <tr>
              <th className="sf-th">{t("backupNameCol")}</th>
              <th className="sf-th">{t("createdCol")}</th>
              <th className="sf-th">{t("sizeCol")}</th>
              <th className="sf-th">{t("filesCol")}</th>
              <th className="sf-th">{t("status")}</th>
              <th className="sf-th text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {history.map((b) => (
              <tr key={b.id} className="sf-tr" data-testid={`backup-row-${b.id}`}>
                <td className="sf-td font-mono-sf text-xs">{b.filename}</td>
                <td className="sf-td">{fmtDateTime(b.created_at)}</td>
                <td className="sf-td">{mb(b.size_bytes)}</td>
                <td className="sf-td">{b.files}</td>
                <td className="sf-td"><span className="rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2.5 py-0.5 uppercase">{b.status}</span></td>
                <td className="sf-td text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={`download-backup-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors" onClick={() => download(`/master/backup/${b.id}/download`, b.filename)}>
                      <Download className="h-3 w-3" /> {t("downloadFull")}
                    </button>
                    <button data-testid={`delete-backup-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors" onClick={() => remove(b)}>
                      <Trash2 className="h-3 w-3" /> {t("remove")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td className="sf-td text-slate-400" colSpan={6}>{t("noBackups")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
